import { fmt } from '@k2pos/core';
import { useNavigate } from 'react-router-dom';
import { HazardStripes } from '@/components/brand/HazardStripes';
import { Button } from '@/components/ui/Button';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { InvoiceCard } from '@/components/InvoiceCard';
import { Mono, Text } from '@/components/ui/Text';
import { IconCheck } from '@/components/icons';
import { can, post } from '@/bridge';
import { useSaveBill } from '@/hooks/useSaveBill';
import { useShopQr } from '@/hooks/useShopQr';
import { usePos } from '@/store/pos';
import { colors } from '@/theme/tokens';
import styles from './Done.module.css';

/** Confirmation plus a shareable bill. Leaving resets the cart for the next sale. */
export function Done() {
  const navigate = useNavigate();
  const sale = usePos((s) => s.lastSale);
  const clearCart = usePos((s) => s.clearCart);
  const shop = usePos((s) => s.shop);
  // Called before the early return below, so the hook order stays stable.
  const qr = useShopQr();
  const { save: saveBill, busy: saving, error: saveError, dismiss } = useSaveBill();

  // `lastSale` is not persisted, so a cold start or a refresh lands here. That
  // is deliberate: re-showing the last invoice would read as a duplicate sale.
  if (!sale) {
    return (
      <div className={styles.centered}>
        <Text size={13} color={colors.textMuted}>
          ບໍ່ມີບິນລ່າສຸດ
        </Text>
        <Button
          label="ກັບໜ້າຫຼັກ"
          className={styles.wide}
          onPress={() => navigate('/', { replace: true })}
        />
      </div>
    );
  }

  const credit = sale.payMode === 'credit';

  function finish() {
    clearCart();
    navigate('/', { replace: true });
  }

  /** Native print when the shell offers it, the browser's own otherwise. */
  function print() {
    if (can('print')) post('print.receipt', { sale });
    else window.print();
  }

  /**
   * The bill as a picture, saved or shared.
   *
   * This replaced a ແຊຣ໌ that sent one line of text — "ຮ້ານ · #A-1042 ·
   * 110,000 ₭" — which is not a bill and cannot be kept as one. A PNG is: the
   * shopkeeper keeps it, or sends it to the customer who is about to transfer,
   * and on a phone the same tap offers both.
   */
  function saveImage() {
    if (!sale) return;
    saveBill({
      shopName: shop.name,
      phone: shop.phone,
      address: shop.address,
      invoiceNo: sale.invoiceNo,
      at: sale.createdAt,
      customerName: sale.customerName,
      credit,
      lines: sale.lines.map((l) => ({
        name: l.name,
        unit: l.unit,
        qty: l.qty,
        amount: Math.round(l.price * l.qty),
      })),
      total: sale.total,
      bank: shop.bankNote,
      qr,
      footer: shop.invoiceFooter,
    });
  }

  return (
    <>
      <div className={styles.success}>
        <HazardStripes className={styles.stripes} opacity={0.3} period={18} />
        <div className={styles.check}>
          <IconCheck size={26} />
        </div>
        <Text weight={800} size={17} color={colors.onDark}>
          ບັນທຶກຂາຍສຳເລັດ
        </Text>
      </div>

      <div className={`${styles.body} vscroll`}>
        <div className={styles.outcome}>
          {credit ? (
            <>
              <Text size={12} color={colors.onDarkBright}>
                ບັນທຶກເປັນໜີ້ຂອງ {sale.customerName}
              </Text>
              <Mono weight={700} size={26} color={colors.sky} className={styles.outcomeAmount}>
                +{fmt(sale.total)} ₭
              </Mono>
              <Text size={11} color={colors.onDarkSoft}>
                ຕັ້ງເຕືອນທວງ · {shop.reminderDays} ມື້
              </Text>
            </>
          ) : (
            <>
              <Text size={12} color={colors.onDarkBright}>
                ຮັບເງິນແລ້ວ
              </Text>
              <Mono weight={700} size={26} color={colors.sky} className={styles.outcomeAmount}>
                {fmt(sale.total)} ₭
              </Mono>
              <Text size={11} color={colors.onDarkSoft}>
                {sale.currency === 'LAK'
                  ? 'ເງິນສົດ / QR'
                  : `ຮັບເປັນ ${sale.currency} · ເກັບເປັນກີບ`}
              </Text>
            </>
          )}
        </div>

        <InvoiceCard
          shopName={shop.name}
          shopPhone={shop.phone}
          shopAddress={shop.address}
          invoiceNo={sale.invoiceNo}
          at={sale.createdAt}
          customerName={sale.customerName}
          credit={credit}
          total={sale.total}
          bank={shop.bankNote}
          qr={qr}
          footer={shop.invoiceFooter}
          lines={sale.lines.map((l) => ({
            productId: l.productId,
            name: l.name,
            unit: l.unit,
            qty: l.qty,
            price: l.price,
            amount: Math.round(l.price * l.qty),
          }))}
        />

        {/* There was an empty grey square here labelled "QR ທະນາຄານຮ້ານ", with
            a line telling the customer to scan it to transfer. Nothing
            generated a QR — there was nothing to scan. The shop's account
            number now prints on the bill itself, which is the thing the
            customer actually leaves with. */}

        <ErrorBanner message={saveError} onDismiss={dismiss} />

        <div className={styles.actions}>
          <Button label="ພິມ" className="grow" onPress={print} />
          <Button
            label={saving ? 'ກຳລັງສ້າງຮູບ…' : 'ບັນທຶກຮູບ'}
            variant="outline"
            className="grow"
            disabled={saving}
            onPress={saveImage}
          />
        </div>

        <Button
          label="ກັບໜ້າຫຼັກ"
          variant="outline"
          className={styles.finish}
          onPress={finish}
        />
      </div>
    </>
  );
}
